import React, { useEffect, useState, useRef } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { Send, Trash2, Download, Upload, Plus, X, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://rentiasi.ro';

export const AdminNewsletterPage = () => {
  const { getAuthHeaders } = useApp();
  const [subscribers, setSubscribers] = useState([]);
  const [newsletter, setNewsletter] = useState({
    subject: { ro: '', en: '', fr: '' },
    content: { ro: '', en: '', fr: '' },
    image_url: '',
  });
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [importing, setImporting] = useState(false);
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [previewLang, setPreviewLang] = useState('ro');
  const [sendLang, setSendLang] = useState('all'); // 'all', 'ro', 'en', 'fr'
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/newsletter/subscribers`, {
        headers: getAuthHeaders(),
      });
      setSubscribers(data);
    } catch (error) {
      toast.error('Failed to fetch subscribers');
    }
  };

  const handleSend = async () => {
    const langLabel = sendLang === 'all' ? 'toate limbile' : sendLang === 'ro' ? 'Română' : sendLang === 'en' ? 'Engleză' : 'Franceză';
    if (!window.confirm(`Trimite newsletter în ${langLabel} la ${subscribers.length} abonați?`)) return;
    setLoading(true);
    try {
      const payload = {
        ...newsletter,
        send_language: sendLang,
        site_url: SITE_URL
      };
      const { data } = await axios.post(`${BACKEND_URL}/api/newsletter/send`, payload, {
        headers: getAuthHeaders(),
      });
      
      // Show detailed result
      if (data.failed > 0) {
        toast.warning(`Trimise: ${data.sent}, Eșuate: ${data.failed}. ${data.failed_emails ? 'Email-uri eșuate: ' + data.failed_emails.join(', ') : ''}`);
      } else {
        toast.success(data.message);
      }
      
      setNewsletter({ subject: { ro: '', en: '', fr: '' }, content: { ro: '', en: '', fr: '' }, image_url: '' });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Eroare la trimiterea newsletter-ului';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subscriber?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/newsletter/subscribers/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success('Subscriber deleted!');
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to delete subscriber');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedSubscribers.length === 0) {
      toast.error('No subscribers selected');
      return;
    }
    if (!window.confirm(`Delete ${selectedSubscribers.length} selected subscribers?`)) return;
    
    try {
      for (const id of selectedSubscribers) {
        await axios.delete(`${BACKEND_URL}/api/newsletter/subscribers/${id}`, {
          headers: getAuthHeaders(),
        });
      }
      toast.success(`${selectedSubscribers.length} subscribers deleted!`);
      setSelectedSubscribers([]);
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to delete some subscribers');
      fetchSubscribers();
    }
  };

  const handleAddSubscriber = async () => {
    if (!newEmail.trim()) {
      toast.error('Please enter an email');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('email', newEmail.trim());
      
      await axios.post(`${BACKEND_URL}/api/newsletter/subscribers/add`, formData, {
        headers: getAuthHeaders(),
      });
      toast.success('Subscriber added!');
      setNewEmail('');
      setShowAddDialog(false);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add subscriber');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await axios.post(`${BACKEND_URL}/api/newsletter/subscribers/import`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success(data.message);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import subscribers');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const toggleSelectSubscriber = (id) => {
    if (selectedSubscribers.includes(id)) {
      setSelectedSubscribers(selectedSubscribers.filter(s => s !== id));
    } else {
      setSelectedSubscribers([...selectedSubscribers, id]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedSubscribers.length === subscribers.length) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(subscribers.map(s => s.id));
    }
  };

  const exportCSV = () => {
    const csv = ['Email,Subscribed At', ...subscribers.map((s) => `${s.email},${s.subscribed_at}`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subscribers.csv';
    a.click();
  };

  return (
    <AdminLayout>
      <div data-testid="admin-newsletter-page">
        <h1 className="text-4xl font-serif mb-8">Newsletter</h1>

        <Tabs defaultValue="compose" className="w-full">
          <TabsList className="border-b border-white/10 bg-transparent p-0 h-auto mb-8">
            <TabsTrigger
              value="compose"
              className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none bg-transparent px-6 py-3"
            >
              Compose
            </TabsTrigger>
            <TabsTrigger
              value="subscribers"
              className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none bg-transparent px-6 py-3"
            >
              Subscribers ({subscribers.length})
            </TabsTrigger>
          </TabsList>

          {/* Compose Tab */}
          <TabsContent value="compose">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Editor */}
              <div className="glass p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-serif">Compose Newsletter</h3>
                  <select
                    onChange={(e) => {
                      const template = e.target.value;
                      if (template === 'new_apartment') {
                        setNewsletter({
                          subject: {
                            ro: '🏠 Apartament Nou Disponibil!',
                            en: '🏠 New Apartment Available!',
                            fr: '🏠 Nouvel Appartement Disponible!'
                          },
                          content: {
                            ro: 'Dragă abonat,\n\nSuntem încântați să vă anunțăm că avem un nou apartament disponibil pentru închiriere!\n\n✨ Caracteristici:\n• Locație premium\n• Complet mobilat\n• Vedere panoramică\n\n💰 Preț special pentru primii chiriași!\n\nVizitați site-ul nostru pentru mai multe detalii și fotografii.\n\nCu stimă,\nEchipa Rentiasi.ro',
                            en: 'Dear subscriber,\n\nWe are excited to announce that we have a new apartment available for rent!\n\n✨ Features:\n• Premium location\n• Fully furnished\n• Panoramic view\n\n💰 Special price for first tenants!\n\nVisit our website for more details and photos.\n\nBest regards,\nRentiasi.ro Team',
                            fr: 'Cher abonné,\n\nNous sommes ravis de vous annoncer qu\'un nouvel appartement est disponible à la location!\n\n✨ Caractéristiques:\n• Emplacement premium\n• Entièrement meublé\n• Vue panoramique\n\n💰 Prix spécial pour les premiers locataires!\n\nVisitez notre site pour plus de détails et photos.\n\nCordialement,\nL\'équipe Rentiasi.ro'
                          },
                          image_url: ''
                        });
                      } else if (template === 'promo') {
                        setNewsletter({
                          subject: {
                            ro: '🎉 Ofertă Specială - Reduceri de Sezon!',
                            en: '🎉 Special Offer - Seasonal Discounts!',
                            fr: '🎉 Offre Spéciale - Réductions Saisonnières!'
                          },
                          content: {
                            ro: 'Dragă abonat,\n\n🔥 OFERTĂ LIMITATĂ! 🔥\n\nProfitați de reducerile noastre de sezon:\n\n✅ 10% reducere la prima lună\n✅ Fără comision de agenție\n✅ Asistență gratuită la mutare\n\n⏰ Oferta este valabilă până la sfârșitul lunii!\n\nNu ratați această oportunitate!\n\nContactați-ne acum pentru mai multe detalii.\n\nCu stimă,\nEchipa Rentiasi.ro',
                            en: 'Dear subscriber,\n\n🔥 LIMITED OFFER! 🔥\n\nTake advantage of our seasonal discounts:\n\n✅ 10% off first month\n✅ No agency commission\n✅ Free moving assistance\n\n⏰ Offer valid until end of month!\n\nDon\'t miss this opportunity!\n\nContact us now for more details.\n\nBest regards,\nRentiasi.ro Team',
                            fr: 'Cher abonné,\n\n🔥 OFFRE LIMITÉE! 🔥\n\nProfitez de nos réductions saisonnières:\n\n✅ 10% de réduction le premier mois\n✅ Sans commission d\'agence\n✅ Aide au déménagement gratuite\n\n⏰ Offre valable jusqu\'à la fin du mois!\n\nNe manquez pas cette opportunité!\n\nContactez-nous maintenant pour plus de détails.\n\nCordialement,\nL\'équipe Rentiasi.ro'
                          },
                          image_url: ''
                        });
                      } else if (template === 'monthly') {
                        setNewsletter({
                          subject: {
                            ro: '📰 Newsletter Lunar - Noutăți și Apartamente',
                            en: '📰 Monthly Newsletter - News & Apartments',
                            fr: '📰 Newsletter Mensuel - Actualités et Appartements'
                          },
                          content: {
                            ro: 'Dragă abonat,\n\nIată noutățile lunii de la Rentiasi.ro:\n\n🏠 APARTAMENTE NOI:\n• 3 apartamente noi în zona Copou\n• 2 studiouri în Centru\n• 1 apartament de lux în Tătărași\n\n📊 PIAȚA IMOBILIARĂ:\n• Cererea pentru închirieri a crescut cu 15%\n• Zonele cele mai căutate: Copou, Centru\n\n💡 SFATUL LUNII:\nVerificați întotdeauna contractul înainte de a semna!\n\nPentru mai multe detalii, vizitați site-ul nostru.\n\nCu stimă,\nEchipa Rentiasi.ro',
                            en: 'Dear subscriber,\n\nHere are this month\'s updates from Rentiasi.ro:\n\n🏠 NEW APARTMENTS:\n• 3 new apartments in Copou area\n• 2 studios in City Center\n• 1 luxury apartment in Tătărași\n\n📊 REAL ESTATE MARKET:\n• Rental demand increased by 15%\n• Most sought-after areas: Copou, Center\n\n💡 TIP OF THE MONTH:\nAlways check the contract before signing!\n\nFor more details, visit our website.\n\nBest regards,\nRentiasi.ro Team',
                            fr: 'Cher abonné,\n\nVoici les actualités du mois de Rentiasi.ro:\n\n🏠 NOUVEAUX APPARTEMENTS:\n• 3 nouveaux appartements à Copou\n• 2 studios au Centre-ville\n• 1 appartement de luxe à Tătărași\n\n📊 MARCHÉ IMMOBILIER:\n• La demande de location a augmenté de 15%\n• Zones les plus recherchées: Copou, Centre\n\n💡 CONSEIL DU MOIS:\nVérifiez toujours le contrat avant de signer!\n\nPour plus de détails, visitez notre site.\n\nCordialement,\nL\'équipe Rentiasi.ro'
                          },
                          image_url: ''
                        });
                      }
                    }}
                    className="bg-slate-900 border border-white/20 px-3 py-2 text-sm focus:outline-none focus:border-primary"
                  >
                    <option value="">📋 Select Template</option>
                    <option value="new_apartment">🏠 New Apartment</option>
                    <option value="promo">🎉 Promo / Offer</option>
                    <option value="monthly">📰 Monthly Newsletter</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Subject</label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-8">RO</span>
                      <input
                        type="text"
                        placeholder="Subiect în Română"
                        value={newsletter.subject.ro}
                        onChange={(e) => setNewsletter({ ...newsletter, subject: { ...newsletter.subject, ro: e.target.value } })}
                        className="flex-1 bg-transparent border border-white/20 px-3 py-2 text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-8">EN</span>
                      <input
                        type="text"
                        placeholder="Subject in English"
                        value={newsletter.subject.en}
                        onChange={(e) => setNewsletter({ ...newsletter, subject: { ...newsletter.subject, en: e.target.value } })}
                        className="flex-1 bg-transparent border border-white/20 px-3 py-2 text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-8">FR</span>
                      <input
                        type="text"
                        placeholder="Sujet en Français"
                        value={newsletter.subject.fr}
                        onChange={(e) => setNewsletter({ ...newsletter, subject: { ...newsletter.subject, fr: e.target.value } })}
                        className="flex-1 bg-transparent border border-white/20 px-3 py-2 text-sm focus:outline-none focus:border-primary"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Content</label>
                  <div className="space-y-3">
                    <div>
                      <span className="text-xs text-muted-foreground">RO - Română</span>
                      <textarea
                        placeholder="Conținut în Română..."
                        value={newsletter.content.ro}
                        onChange={(e) => setNewsletter({ ...newsletter, content: { ...newsletter.content, ro: e.target.value } })}
                        rows={6}
                        className="w-full mt-1 bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
                      />
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">EN - English</span>
                      <textarea
                        placeholder="Content in English..."
                        value={newsletter.content.en}
                        onChange={(e) => setNewsletter({ ...newsletter, content: { ...newsletter.content, en: e.target.value } })}
                        rows={6}
                        className="w-full mt-1 bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
                      />
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">FR - Français</span>
                      <textarea
                        placeholder="Contenu en Français..."
                        value={newsletter.content.fr}
                        onChange={(e) => setNewsletter({ ...newsletter, content: { ...newsletter.content, fr: e.target.value } })}
                        rows={6}
                        className="w-full mt-1 bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Header Image URL (Optional)</label>
                  <input
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={newsletter.image_url}
                    onChange={(e) => setNewsletter({ ...newsletter, image_url: e.target.value })}
                    className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm focus:outline-none focus:border-primary"
                  />
                </div>

                {/* Language Selection for Sending */}
                <div>
                  <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Limba de Trimitere</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'all', label: '🌍 Toate limbile' },
                      { value: 'ro', label: '🇷🇴 Doar Română' },
                      { value: 'en', label: '🇬🇧 Doar Engleză' },
                      { value: 'fr', label: '🇫🇷 Doar Franceză' }
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setSendLang(opt.value)}
                        className={`px-3 py-2 text-sm rounded border transition-colors ${
                          sendLang === opt.value
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-slate-800 text-muted-foreground border-white/20 hover:border-primary/50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {sendLang === 'all' 
                      ? 'Email-ul va fi trimis în toate cele 3 limbi' 
                      : `Email-ul va fi trimis doar în ${sendLang === 'ro' ? 'Română' : sendLang === 'en' ? 'Engleză' : 'Franceză'}`
                    }
                  </p>
                </div>

                <Button
                  onClick={handleSend}
                  disabled={loading || !newsletter.subject[sendLang === 'all' ? 'ro' : sendLang] || !newsletter.content[sendLang === 'all' ? 'ro' : sendLang]}
                  data-testid="send-newsletter-button"
                  className="w-full bg-primary text-primary-foreground"
                >
                  <Send size={18} className="mr-2" />
                  {loading ? 'Se trimite...' : `Trimite la ${subscribers.length} Abonați`}
                </Button>
              </div>

              {/* Live Preview */}
              <div className="glass p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-serif flex items-center gap-2">
                    <Globe size={18} />
                    Previzualizare Email
                  </h3>
                  <div className="flex gap-2">
                    {['ro', 'en', 'fr'].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => setPreviewLang(lang)}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                          previewLang === lang
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-slate-800 text-muted-foreground hover:bg-slate-700'
                        }`}
                      >
                        {lang.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="bg-white text-black rounded-lg overflow-hidden shadow-xl">
                  {/* Email Header */}
                  <div className="bg-gradient-to-r from-amber-600 to-amber-500 p-6 text-center">
                    <h1 className="text-2xl font-bold text-white tracking-wide">RENTIASI.RO</h1>
                    <p className="text-amber-100 text-sm mt-1">
                      {previewLang === 'ro' && 'Apartamente de Închiriat în Iași'}
                      {previewLang === 'en' && 'Apartments for Rent in Iași'}
                      {previewLang === 'fr' && 'Appartements à Louer à Iași'}
                    </p>
                  </div>
                  
                  {/* Header Image */}
                  {newsletter.image_url && (
                    <img src={newsletter.image_url} alt="" className="w-full h-48 object-cover" />
                  )}
                  
                  {/* Email Content */}
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                      {newsletter.subject[previewLang] || `Subiect în ${previewLang.toUpperCase()}...`}
                    </h2>
                    <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                      {newsletter.content[previewLang] || `Conținutul în ${previewLang.toUpperCase()} va apărea aici...`}
                    </div>
                    
                    {/* CTA Button */}
                    <div className="mt-6 text-center">
                      <a 
                        href={`${SITE_URL}/apartments`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block bg-amber-500 text-white px-6 py-3 rounded font-semibold text-sm hover:bg-amber-600 transition-colors"
                      >
                        {previewLang === 'ro' && 'Vezi Apartamente →'}
                        {previewLang === 'en' && 'View Apartments →'}
                        {previewLang === 'fr' && 'Voir Appartements →'}
                      </a>
                    </div>
                  </div>
                  
                  {/* Email Footer */}
                  <div className="bg-gray-100 p-4 text-center border-t">
                    <p className="text-xs text-gray-500">
                      © 2025 Rentiasi.ro | Iași, România
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {previewLang === 'ro' && 'Ați primit acest email deoarece v-ați abonat la newsletter-ul nostru.'}
                      {previewLang === 'en' && 'You received this email because you subscribed to our newsletter.'}
                      {previewLang === 'fr' && 'Vous avez reçu cet email car vous êtes abonné à notre newsletter.'}
                    </p>
                    <p className="text-xs text-amber-600 mt-2 underline cursor-pointer">
                      {previewLang === 'ro' && 'Dezabonare'}
                      {previewLang === 'en' && 'Unsubscribe'}
                      {previewLang === 'fr' && 'Se désabonner'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers">
            <div className="glass p-6">
              <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                <h3 className="text-xl font-serif">Subscriber List</h3>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => setShowAddDialog(true)} size="sm" className="bg-primary text-primary-foreground">
                    <Plus size={16} className="mr-2" />
                    Add Subscriber
                  </Button>
                  <Button onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" disabled={importing}>
                    <Upload size={16} className="mr-2" />
                    {importing ? 'Importing...' : 'Import CSV'}
                  </Button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImport}
                    accept=".csv,.txt"
                    className="hidden"
                  />
                  <Button onClick={exportCSV} variant="outline" size="sm">
                    <Download size={16} className="mr-2" />
                    Export CSV
                  </Button>
                  {selectedSubscribers.length > 0 && (
                    <Button onClick={handleDeleteSelected} variant="destructive" size="sm">
                      <Trash2 size={16} className="mr-2" />
                      Delete ({selectedSubscribers.length})
                    </Button>
                  )}
                </div>
              </div>

              {/* Import instructions */}
              <div className="mb-4 p-3 bg-slate-900/50 border border-white/10 text-xs text-muted-foreground">
                <strong className="text-primary">Import Format:</strong> CSV file with emails in the first column (one per line). 
                Example: email@example.com
              </div>

              {subscribers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-12">No subscribers yet</p>
              ) : (
                <div className="space-y-2">
                  {/* Select All */}
                  <div className="flex items-center gap-3 p-3 bg-slate-800/50 border border-white/10">
                    <input
                      type="checkbox"
                      checked={selectedSubscribers.length === subscribers.length && subscribers.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-xs text-muted-foreground">
                      {selectedSubscribers.length === subscribers.length ? 'Deselect All' : 'Select All'}
                    </span>
                  </div>
                  
                  {subscribers.map((sub) => (
                    <div
                      key={sub.id}
                      data-testid={`subscriber-item-${sub.id}`}
                      className={`flex items-center justify-between p-4 border transition-colors ${
                        selectedSubscribers.includes(sub.id) 
                          ? 'bg-primary/10 border-primary/30' 
                          : 'bg-slate-900/50 border-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <input
                          type="checkbox"
                          checked={selectedSubscribers.includes(sub.id)}
                          onChange={() => toggleSelectSubscriber(sub.id)}
                          className="w-4 h-4 accent-primary"
                        />
                        <div>
                          <p className="text-sm">{sub.email}</p>
                          <p className="text-xs text-muted-foreground">Subscribed: {new Date(sub.subscribed_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDelete(sub.id)}
                        data-testid={`delete-subscriber-${sub.id}`}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Add Subscriber Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-slate-950 border-white/10">
            <DialogHeader>
              <DialogTitle className="text-xl font-serif">Add Subscriber</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Email Address</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-sm focus:outline-none focus:border-primary"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSubscriber()}
                />
              </div>
              <div className="flex gap-3">
                <Button onClick={handleAddSubscriber} className="flex-1 bg-primary text-primary-foreground">
                  Add Subscriber
                </Button>
                <Button onClick={() => setShowAddDialog(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminNewsletterPage;